import { Injectable } from '@angular/core';
import { GameQuestion, QuizSubject, DifficultyLevel } from '../interfaces/question.interface';
import { QUESTION_BANK } from '../data/question-bank';
import { QUESTIONS_PER_SESSION } from '../data/league-config';

@Injectable({ providedIn: 'root' })
export class QuestionBankService {

  private usedQuestionIds: Set<string> = new Set();

  getQuestions(params: {
    subject?: QuizSubject | 'random';
    gradeLevel?: number;
    difficulty?: DifficultyLevel;
    count?: number;
  }): GameQuestion[] {
    const count = params.count || QUESTIONS_PER_SESSION;
    let pool = [...QUESTION_BANK];

    // Filter by subject
    if (params.subject && params.subject !== 'random') {
      pool = pool.filter(q => q.subject === params.subject);
    }

    // Filter by grade level
    if (params.gradeLevel) {
      pool = pool.filter(q => params.gradeLevel >= q.gradeRange.min && params.gradeLevel <= q.gradeRange.max);
    }

    // Filter by difficulty
    if (params.difficulty) {
      pool = pool.filter(q => q.difficulty === params.difficulty);
    }

    // Prefer unused questions
    const unused = pool.filter(q => !this.usedQuestionIds.has(q.id));
    const sourcePool = unused.length >= count ? unused : pool;

    // Shuffle and pick
    const shuffled = this.shuffle(sourcePool);
    const selected = shuffled.slice(0, count);

    // Track used questions
    selected.forEach(q => this.usedQuestionIds.add(q.id));

    // Reset tracking if we've used most questions
    if (this.usedQuestionIds.size > QUESTION_BANK.length * 0.8) {
      this.usedQuestionIds.clear();
    }

    return selected;
  }

  getAvailableSubjects(gradeLevel?: number): QuizSubject[] {
    let pool = QUESTION_BANK;
    if (gradeLevel) {
      pool = pool.filter(q => gradeLevel >= q.gradeRange.min && gradeLevel <= q.gradeRange.max);
    }
    const subjects = new Set(pool.map(q => q.subject));
    return Array.from(subjects);
  }

  getQuestionCountBySubject(gradeLevel?: number): Record<string, number> {
    let pool = QUESTION_BANK;
    if (gradeLevel) {
      pool = pool.filter(q => gradeLevel >= q.gradeRange.min && gradeLevel <= q.gradeRange.max);
    }
    const counts: Record<string, number> = {};
    pool.forEach(q => {
      counts[q.subject] = (counts[q.subject] || 0) + 1;
    });
    return counts;
  }

  resetUsedQuestions(): void {
    this.usedQuestionIds.clear();
  }

  private shuffle<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }
}
